import useTimeFilter from 'components/ReportTimeRangePicker/useTimeFilter';
import {
  TransactionDataFragment,
  useGetAllEventTransactionsQuery,
  useGetAllWaitingLineTransactionsQuery,
} from 'lib/graphql/graphql';
import { mergePaginatedList, useManualPagination } from 'lib/graphql/pagination';
import useEvent from 'providers/event/useEvent';
import { useMemo } from 'react';

interface QueryInfos {
  loading: boolean
  hasNextPage: boolean
  fetchMore: () => Promise<any>
}

function useTransactionsQuery(selectedTime: string, selectedWaitingLine: string) {
  const { id: eventId, timezone } = useEvent();

  const filter = useTimeFilter(selectedTime, timezone);

  const eventQuery = useManualPagination(
    useGetAllEventTransactionsQuery({
      variables: { eventId, filter },
      skip: selectedWaitingLine !== 'all',
    }),
    (d) => d.getAllEventTransactions.lastEvaluated,
    (existing, incoming) => ({
      getAllEventTransactions: mergePaginatedList(
        existing.getAllEventTransactions,
        incoming.getAllEventTransactions,
        'items',
      ),
    }),
  );

  const waitingLineQuery = useManualPagination(
    useGetAllWaitingLineTransactionsQuery({
      variables: { waitingLineId: selectedWaitingLine, filter },
      skip: selectedWaitingLine === 'all',
    }),
    (d) => d.getAllWaitingLineTransactions.lastEvaluated,
    (existing, incoming) => ({
      getAllWaitingLineTransactions: mergePaginatedList(
        existing.getAllWaitingLineTransactions,
        incoming.getAllWaitingLineTransactions,
        'items',
      ),
    }),
  );

  return useMemo<[TransactionDataFragment[], QueryInfos]>(() => {
    if (selectedWaitingLine !== 'all') {
      return [waitingLineQuery.data?.getAllWaitingLineTransactions.items || [], waitingLineQuery];
    }
    return [eventQuery.data?.getAllEventTransactions.items || [], eventQuery];
  }, [eventQuery, selectedWaitingLine, waitingLineQuery]);
}

export default useTransactionsQuery;
